import React from 'react';
import Spinner from './Spinner.js';
import { Paperclip, FileText, ExternalLink, Eye } from 'lucide-react';

const OrderPreview = ({ order, customer, firms }) => {
  if (!order || !customer || !firms.length) {
    return <div style={{padding: '20px', textAlign: 'center'}}><Spinner /></div>;
  }
  
  const selectedFirm = firms.find(f => f.id === order.firmId);
  if (!selectedFirm) return <p>Firm details not found.</p>;

  const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.address || ""}, ${addr.city || ""}, ${addr.state || ""} - ${addr.pincode || ""}`;
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return '';
    try {
        const decodedUrl = decodeURIComponent(url);
        const pathParts = decodedUrl.split('?')[0].split('/');
        const fileNameWithPrefix = pathParts[pathParts.length - 1];
        const underscoreIndex = fileNameWithPrefix.indexOf('_');
        if (underscoreIndex !== -1) {
            if (/^\d{13,}/.test(fileNameWithPrefix)) {
                return fileNameWithPrefix.substring(underscoreIndex + 1);
            }
            const lastUnderscoreIndex = fileNameWithPrefix.lastIndexOf('_');
            if (lastUnderscoreIndex !== -1 && /\d{13,}/.test(fileNameWithPrefix.substring(lastUnderscoreIndex + 1))) {
                return fileNameWithPrefix.substring(0, lastUnderscoreIndex);
            }
        }
        return fileNameWithPrefix;
    } catch (e) {
        return 'Attached File';
    }
  };

  const formattedOrderDate = new Date(order.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const formattedQuoteDate = order.quoteDate ? new Date(order.quoteDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const formattedWoDate = order.woDate ? new Date(order.woDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const formattedPoDate = order.poDate ? new Date(order.poDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const formatAttachmentDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // CSS exactly matched from OrderDetail.js handleExportPdf
  const containerStyle = { width: '100%', maxWidth: '1000px', margin: '0 auto', fontFamily: 'Arial, sans-serif', color: '#1b1b1b', backgroundColor: 'white', boxSizing: 'border-box' };
  const tableStyle = { width: '100%', borderCollapse: 'collapse' };
  const headerTableStyle = { ...tableStyle, border: '1px solid #1b1b1b' };
  const headerTableCellStyle = { padding: '10px', verticalAlign: 'middle' };
  const upperTableTdStyle = { padding: '8px', textAlign: 'left', border: '1px solid #1b1b1b', fontSize: '16px' };
  const detailLabelCellStyle = { ...upperTableTdStyle, width: '18.75%' };
  const detailValueCellStyle = { ...upperTableTdStyle, width: '31.25%' };
  const tableTitleStyle = { backgroundColor: '#f2f2f2', fontWeight: 'bold' };
  const itemTableThStyle = { border: '1px solid #1b1b1b', padding: '8px', verticalAlign: 'top', backgroundColor: '#f9f9f9', textAlign: 'left', fontSize: '16px' };
  const itemTableTdStyle = { border: '1px solid #1b1b1b', padding: '8px', verticalAlign: 'top', fontSize: '16px' };
  const summaryTableStyle = { ...tableStyle, border: '1px solid #1b1b1b', marginTop: '20px' };
  const summaryTableTdStyle = { padding: '12px', fontSize: '16px', border: '1px solid #1b1b1b' };

  // Attachment Section Styles
  const cardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginTop: '30px', border: '1px solid #e5e7eb' };
  const attachmentCardStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    textDecoration: 'none',
    color: 'inherit',
    transition: 'all 0.2s'
  };
  const attachmentIconStyle = (bg = '#eff6ff', color = '#2563eb') => ({
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: bg,
    color: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  });
  const attachmentLabelStyle = { fontSize: '13px', fontWeight: '600', color: '#374151' };
  const attachmentNameStyle = { fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' };

  return (
    <div style={containerStyle}>
      {/* Header Table */}
      <table style={headerTableStyle}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, borderRight: '1px solid #1b1b1b', width: '20%' }}>
              <img width="150px" src={selectedFirm.logo} alt="Logo" />
            </td>
            <td style={{ ...headerTableCellStyle, width: '50%', fontSize: '14px' }}>
              <strong>{selectedFirm.name}</strong>,<br />
              {selectedFirm.address}<br />
              GSTIN - {selectedFirm.gstin}<br />
              E-Mail - {selectedFirm.email}<br />
              Tel: {selectedFirm.tel}
            </td>
            <td style={{ ...headerTableCellStyle, textAlign: 'right', verticalAlign: 'top' }}>
              <p style={{ fontSize: '25px', fontWeight: 'bold', margin: 0 }}>{order.orderType.toUpperCase()} ORDER</p>
              {order.orderNumber || order.id}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Upper Table */}
      <table style={{ ...tableStyle, borderCollapse: 'collapse' }}>
        <tbody>
          <tr style={tableTitleStyle}>
            <td colSpan="4" style={upperTableTdStyle}><strong>Order Details:</strong></td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Date:</strong></td><td style={detailValueCellStyle}>{formattedOrderDate}</td>
            <td style={detailLabelCellStyle}><strong>Quote Number:</strong></td><td style={detailValueCellStyle}>{order.quoteNumber || "N/A"}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Order ID:</strong></td><td style={detailValueCellStyle}>{order.orderNumber || order.id}</td>
            <td style={detailLabelCellStyle}><strong>Quote Date:</strong></td><td style={detailValueCellStyle}>{formattedQuoteDate}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Salesperson:</strong></td><td style={detailValueCellStyle}>{order.salesPerson}</td>
            <td style={detailLabelCellStyle}><strong>{order.orderType === 'Sales' ? 'PO Number' : 'Work Order Number'}:</strong></td><td style={detailValueCellStyle}>{order.orderType === 'Sales' ? order.poNumber : order.workOrderNumber || ''}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Project Name:</strong></td><td style={detailValueCellStyle}>{order.projectName || ''}</td>
            <td style={detailLabelCellStyle}><strong>{order.orderType === 'Sales' ? 'PO Date' : 'Work Order Date'}:</strong></td><td style={detailValueCellStyle}>{order.orderType === 'Sales' ? formattedPoDate : formattedWoDate || ''}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Project Location:</strong></td><td style={detailValueCellStyle}>{order.projectLocation?.address || ''}</td>
            <td style={detailLabelCellStyle}><strong>{order.orderType === 'Sales' ? 'Payment Terms:' : 'Rental Tenure:'}</strong></td>
            <td style={detailValueCellStyle}>
              {order.orderType === 'Sales' ? (customer?.payment_terms || 'N/A') : (order.rentalTenureMonths + ' Months')}
            </td>
          </tr>
          {order.orderType === 'Rental' && (
            <>
              <tr>
                <td style={detailLabelCellStyle}><strong>Rental Agreement:</strong></td><td style={detailValueCellStyle}>{order.rentalAgreementReceived ? 'Received' : 'Pending'}</td>
                <td style={detailLabelCellStyle}><strong>Payment Terms:</strong></td><td style={detailValueCellStyle}>{customer?.payment_terms || 'N/A'}</td>
              </tr>
              <tr>
                <td style={detailLabelCellStyle}><strong>Security Cheque:</strong></td><td style={detailValueCellStyle}>{order.securityChequeReceived ? 'Received' : 'Pending'}</td>
                <td style={detailLabelCellStyle}><strong>Background Verif:</strong></td><td style={detailValueCellStyle}>{order.backgroundVerificationDone ? `Done (${order.backgroundVerificationDoneBy})` : 'Pending'}</td>
              </tr>
            </>
          )}
          
          <tr style={tableTitleStyle}>
            <td style={{ ...upperTableTdStyle, width: '33%' }}><strong>Logistics Details</strong></td>
            <td style={{ ...upperTableTdStyle, width: '33%' }}><strong>Installation Details</strong></td>
            <td style={{ ...upperTableTdStyle, width: '34%' }} colSpan="2"><strong>Material Checklist</strong></td>
          </tr>
          <tr>
            <td style={{ ...upperTableTdStyle, verticalAlign: 'top' }}>{order.logisticsDetails || 'N/A'}</td>
            <td style={{ ...upperTableTdStyle, verticalAlign: 'top' }}>{order.installationDetails || 'N/A'}</td>
            <td style={{ ...upperTableTdStyle, verticalAlign: 'top' }} colSpan="2">
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Stickers: {order.materialChecklist?.stickers ? 'Yes' : 'No'}</li>
                <li>Laser Marking: {order.materialChecklist?.laserMarking ? 'Yes' : 'No'}</li>
                <li>Material Wrapping: {order.materialChecklist?.materialWrapping ? 'Yes' : 'No'}</li>
                {order.materialChecklist?.materialInstructions ? <li>Instructions: {order.materialChecklist.materialInstructions}</li> : ''}
              </ul>
            </td>
          </tr>

          <tr style={tableTitleStyle}>
            <td colSpan="2" style={upperTableTdStyle}><strong>Billing Details:</strong></td>
            <td colSpan="2" style={upperTableTdStyle}><strong>Shipping Details:</strong></td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Company Name</strong></td><td style={detailValueCellStyle}>{customer.company_name || customer.display_name}</td>
            <td style={detailLabelCellStyle}><strong>Ship to:</strong></td><td style={detailValueCellStyle}>{order.shippingAddress?.attention || customer.display_name}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Address</strong></td><td style={detailValueCellStyle}>{formatAddress(order.billingAddress)}</td>
            <td style={detailLabelCellStyle}><strong>Address</strong></td><td style={detailValueCellStyle}>{formatAddress(order.shippingAddress)}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Contact Person</strong></td><td style={detailValueCellStyle}>{order.billingAddress?.attention || ''}</td>
            <td style={detailLabelCellStyle}><strong>Contact Person</strong></td><td style={detailValueCellStyle}>{order.shippingAddress?.attention || ''}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Contact Number</strong></td><td style={detailValueCellStyle}>{order.billingAddress?.phone || ''}</td>
            <td style={detailLabelCellStyle}><strong>Contact Number</strong></td><td style={detailValueCellStyle}>{order.shippingAddress?.phone || ''}</td>
          </tr>
          <tr>
            <td style={detailLabelCellStyle}><strong>Contact Email</strong></td><td style={detailValueCellStyle}>{order.billingAddress?.email || ''}</td>
            <td style={detailLabelCellStyle}><strong>Contact Email</strong></td><td style={detailValueCellStyle}>{order.shippingAddress?.email || ''}</td>
          </tr>
        </tbody>
      </table>

      {/* Summary Table */}
      <table style={summaryTableStyle}>
        <tbody>
          <tr>
            <td style={{ ...summaryTableTdStyle, fontWeight: 'bold', textAlign: 'right' }}>
              Total Order Value:<br />
              <strong>Rs.{(order.orderValue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            </td>
            <td style={{ ...summaryTableTdStyle, fontWeight: 'bold', textAlign: 'right', width: '30%' }}>
              Payment Status:<br />
              {order.paymentStatus || "N/A"}
            </td>
          </tr>
        </tbody>
      </table>

      {/* BOMs */}
      {order.boms.map((bom, idx) => (
        <div key={idx}>
          <h2 style={{ marginTop: '20px', marginBottom: '10px', textTransform: 'uppercase', fontWeight: 'bold' }}>{bom.name}</h2>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={itemTableThStyle}>Sr. No.</th>
                <th style={itemTableThStyle}>SKU</th>
                <th style={{ ...itemTableThStyle, width: '50%' }}>Item</th>
                <th style={{ ...itemTableThStyle, textAlign: 'right' }}>Quantity Per Set</th>
                <th style={{ ...itemTableThStyle, textAlign: 'right' }}>Total Qty</th>
              </tr>
            </thead>
            <tbody>
              {bom.components.map((comp, index) => (
                <tr key={index}>
                  <td style={itemTableTdStyle}>{index + 1}</td>
                  <td style={itemTableTdStyle}>{comp.sku || "N/A"}</td>
                  <td style={itemTableTdStyle}>{comp.name}</td>
                  <td style={{ ...itemTableTdStyle, textAlign: 'right' }}>{comp.baseQty || 0}</td>
                  <td style={{ ...itemTableTdStyle, textAlign: 'right' }}>{Math.round(comp.baseQty * bom.multiplier)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f2f2f2' }}>
                <td colSpan="4" style={{ ...itemTableTdStyle, textAlign: 'right', padding: '8px' }}>Total Quantity:</td>
                <td style={{ ...itemTableTdStyle, textAlign: 'right', padding: '8px' }}>
                  {bom.components.reduce((sum, comp) => sum + Math.round((comp.baseQty || 0) * bom.multiplier), 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}

      {/* --- ORDER ATTACHMENTS SECTION --- */}
      {(order.quoteFileUrl || order.poFileUrl || order.workOrderFileUrl || order.rentalAgreementFileUrl || order.securityChequeFileUrl) && (
        <div className="no-print" style={cardStyle}>
          <h3 style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', fontSize: '16px', fontWeight: 'bold', color: '#1e293b'}}>
            <Paperclip size={20} color="#2563eb"/> Order Attachments
          </h3>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
            {order.quoteFileUrl && (
              <a href={order.quoteFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle()}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>Quote Document</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(order.quoteFileUrl)}</div>
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            )}
            {order.poFileUrl && (
              <a href={order.poFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle('#dcfce7', '#166534')}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>Purchase Order (PO)</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(order.poFileUrl)}</div>
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            )}
            {order.workOrderFileUrl && (
              <a href={order.workOrderFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle('#fef9c3', '#854d0e')}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>Work Order</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(order.workOrderFileUrl)}</div>
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            )}
            {order.rentalAgreementFileUrl && (
              <a href={order.rentalAgreementFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle('#ede9fe', '#5b21b6')}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>Rental Agreement</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(order.rentalAgreementFileUrl)}</div>
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            )}
            {order.securityChequeFileUrl && (
              <a href={order.securityChequeFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle('#fee2e2', '#991b1b')}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>Security Cheque</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(order.securityChequeFileUrl)}</div>
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            )}
          </div>
        </div>
      )}

      {order.additionalAttachments?.length > 0 && (
        <div className="no-print" style={cardStyle}>
          <h3 style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', fontSize: '16px', fontWeight: 'bold', color: '#1e293b'}}>
            <Paperclip size={20} color="#2563eb"/> Additional Attachments
          </h3>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
            {order.additionalAttachments.map((file, index) => (
              <a key={`${file.url || file.name}-${index}`} href={file.url} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={attachmentIconStyle('#e0f2fe', '#0369a1')}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>{file.label || 'Additional Attachment'}</div>
                  <div style={attachmentNameStyle}>{file.name || getFileNameFromUrl(file.url)}</div>
                  {file.loggedAt && <div style={{...attachmentNameStyle, marginTop: '4px'}}>Logged: {formatAttachmentDateTime(file.loggedAt)}</div>}
                </div>
                <Eye size={18} color="#6b7280"/>
              </a>
            ))}
          </div>
        </div>
      )}
      <style dangerouslySetInnerHTML={{ __html: `@media print { .no-print { display: none !important; } }`}} />
    </div>
  );
};

export default OrderPreview;
